import { StandardProps } from "../componentMap";
import { YourekoUI } from "./YourekoUI";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { dispatchAnalyticsEvent } from "../../utils";
import { useEffect, useRef } from "react";
import { getOwnBrand } from "../../utils/product/getOwnBrand";
import { sanitiseTitle } from "../Title/sanitiseTitle";
import { getDefaultCategory } from "../../utils/product/getDefaultCategory";

const eventsToCapture = ["views"];

const computingCategories = [
  "View All Desktop PCs & iMacs",
  "View All Laptops & MacBooks",
  "View All Coffee Machines",
  "Vacuum Cleaners",
  "View All Tablets",
  "View All Mobile Phones",
  "View All TVs",
  "View All Smart Watches",
];

const excludedCategories = [
  "Cooker Hoods",
  "Cookers",
  "Hobs",
  "Built in Ovens",
  "Microwave Ovens",
  "Warming Drawers",
  ...computingCategories,
];

const getAnalyticsPayload = (
  brandName: string | undefined,
  category: string | undefined,
  skuId: string,
  price: string | undefined,
  productName: string,
  productId: string,
) => ({
  type: "pdp",
  subType: "yourEko - view modal",
  additionalInfo: {
    soPost: "true",
  },
  properties: {},
  products: [
    {
      brand: brandName,
      category: category,
      sku: skuId,
      price: price,
      productName: productName,
      productId: productId,
    },
  ],
});

export function Youreko({ product }: Pick<StandardProps, "product">) {
  const {
    brand,
    aliases: { pimProductId },
  } = product;

  const {
    variant: {
      aliases: { skuId },
      price,
      title,
    },
  } = useVariant(product);

  const ownBrand = getOwnBrand(product);
  const sanitisedTitle = sanitiseTitle(title, ownBrand);
  const defaultCategory = getDefaultCategory(product);
  const capturedEvents = useRef<Array<string>>([]);

  useEffect(() => {
    if (excludedCategories.includes(defaultCategory)) return;

    capturedEvents.current = [];
    window.raiseYourekoEvent = (eventName) => {
      if (
        eventsToCapture.includes(eventName) &&
        !capturedEvents.current.includes(eventName)
      ) {
        capturedEvents.current.push(eventName);

        dispatchAnalyticsEvent(
          "global.interaction",
          getAnalyticsPayload(
            brand?.name,
            defaultCategory,
            skuId,
            price?.value,
            sanitisedTitle,
            pimProductId,
          ),
          {},
        );
      }
    };

    return () => {
      window.raiseYourekoEvent = undefined;
    };
  }, [skuId]);

  if (excludedCategories.includes(defaultCategory)) return null;

  return <YourekoUI />;
}
